import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useGallery,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { pickHeroImage } from '../utils/heroImageUtils';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  /**
   * Full SEO subtitle sentence rendered as an h2 under the page title.
   * Use {{field_name}} placeholders for property fields (e.g.
   * {{property_name}}, {{property_address_city}}) — expanded into richtext
   * field tokens for the default value.
   */
  subtitle?: string;
  blurbField?: string;
  defaultText?: string;
  /**
   * When true (default), render a solid brand-primary background instead of a
   * photographic image. Set false only if a page needs a photo hero.
   */
  solidBackground?: boolean;
}

const FIELD = (field: string) =>
  `<span data-type="field-token" data-field="${field}"></span>`;

/** Expands {{field}} placeholders into richtext field tokens and wraps in <p>. */
function buildSubtitleHtml(template: string): string {
  const expanded = template.replace(/\{\{(\w+)\}\}/g, (_, field: string) => FIELD(field));
  return `<p>${expanded}</p>`;
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function hasBlurbContent(value: string | null | undefined): boolean {
  return Boolean(stripHtml(value || '').trim());
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  subtitle,
  blurbField,
  defaultText,
  solidBackground = true,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const blurbPath = `${prefix}.blurb`;
  const bgPath = `${prefix}.bg_image`;

  const subtitleDefault = buildSubtitleHtml(subtitle || 'Explore this page');

  const blurbContent = useContentToken(blurbPath, {
    defaultField: blurbField,
    defaultValue: defaultText || '',
  });
  const showBlurb = hasBlurbContent(blurbContent);
  const bgImage = useSlotGroup(bgPath, bgImageDef);
  const gallery = useGallery();
  const featuredImage = usePropertyField('property_featured_image');

  const pageHint = prefix;
  const keywords = useMemo(
    () => title.toLowerCase().split(/\s+/).filter(Boolean),
    [title],
  );

  const photoIndex = useMemo(
    () => pickHeroImage(gallery, keywords, pageHint),
    [gallery, keywords, pageHint],
  );

  const displayImage =
    bgImage.image ||
    (photoIndex >= 0 ? gallery[photoIndex]?.image : null) ||
    featuredImage ||
    gallery[0]?.image ||
    null;

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="montserrat"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'montserrat');
    document.head.appendChild(link);
  }, []);

  return (
    <section className="hero-solid-bg">
      {/* Full-bleed photo band matching the homepage Hero: featured image +
          title/subtitle/blurb, with a shallow V (chevron) clipped into the
          next section. No paint border. */}
      <div className="hero-solid-hero hero-rail-anchor relative w-full overflow-hidden">
        {solidBackground ? (
          <div
            className="absolute inset-0"
            style={{ backgroundColor: 'var(--vibe-color-primary)' }}
            aria-hidden="true"
          />
        ) : (
          <>
            <EditableSlotGroup groupPath={bgPath} definition={bgImageDef} className="absolute inset-0">
              {displayImage ? (
                <VibesitesImage
                  url={displayImage}
                  alt={bgImage.alt || title}
                  priority
                  sizes="100vw"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-[var(--vibe-color-text)]" />
              )}
            </EditableSlotGroup>

            {/* Soft dark overlay — same treatment as the homepage hero so
                white type stays readable over the featured photo. */}
            <div className="hero-solid-overlay absolute inset-0" aria-hidden="true" />
          </>
        )}

        <div className="hero-solid-title-wrap relative z-10 flex h-full flex-col items-center justify-center gap-3 sm:gap-4 px-6 sm:px-8 text-center text-white">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-solid-title"
          />
          <RichTextContent
            path={subtitlePath}
            label="SEO Subtitle"
            defaultValue={subtitleDefault}
            as="h2"
            className="hero-solid-seo-subtitle"
          />
          {showBlurb ? (
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="hero-solid-subtitle font-body max-w-4xl [&_a]:underline"
            />
          ) : null}
        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive. */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-solid-bg {
          isolation: isolate;
          --hero-v-depth: clamp(2rem, 4vw, 3.5rem);
          background: var(--vibe-color-background, #fff);
        }
        @media (min-width: 768px) {
          .hero-solid-bg {
            --hero-v-depth: clamp(2.25rem, 3.5vw, 4rem);
          }
        }

        .hero-solid-hero {
          /* Rail tile size matches the home hero (1/5 viewport), not this shorter hero. */
          --hero-rail-slot-count: 5;
          --hero-rail-width: max(
            168px,
            min(280px, calc(100vh / 5))
          );
          --hero-rail-hamburger-height: var(--hero-rail-width);
          /* Compact band: clears the fixed header, fits title + blurb, and
             stays shorter than the previous half-viewport floor. */
          height: auto;
          min-height: max(200px, 28vh);
          min-height: max(200px, 28svh);
          min-height: max(200px, 28dvh);
          /* Wide shallow V — same chevron the homepage Hero uses. Tip sits
             on the bottom edge at page center (ApartmentSync equalizes its
             Map / Floor Plans / List tabs so the middle heading lines up). */
          clip-path: polygon(
            0 0,
            100% 0,
            100% calc(100% - var(--hero-v-depth)),
            50% 100%,
            0 calc(100% - var(--hero-v-depth))
          );
        }
        @supports (height: 100dvh) {
          .hero-solid-hero {
            --hero-rail-width: max(
              168px,
              min(280px, calc(100dvh / 5))
            );
          }
        }

        .hero-solid-overlay {
          background: linear-gradient(
            180deg,
            rgba(18, 22, 30, 0.55) 0%,
            rgba(18, 22, 30, 0.42) 45%,
            rgba(18, 22, 30, 0.5) 100%
          );
        }

        /* Fixed transparent header sits over this hero. Top padding clears
           the utility strip + nav so title/subtitle never sit under it.
           Bottom padding clears the V tip so copy stays above the chevron. */
        .hero-solid-title-wrap {
          padding-top: calc(var(--offer-banner-height, 0px) + 7.5rem);
          padding-bottom: calc(var(--hero-v-depth) + 1.5rem);
          box-sizing: border-box;
        }
        @media (min-width: 768px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + 8.5rem);
            padding-bottom: calc(var(--hero-v-depth) + 2rem);
          }
        }
        @media (min-width: 1024px) {
          .hero-solid-title-wrap {
            padding-top: calc(var(--offer-banner-height, 0px) + 9rem);
          }
        }
        .hero-solid-title {
          font-family: "Montserrat", var(--vibe-font-heading, sans-serif);
          font-size: 34px;
          font-weight: 400;
          line-height: 1.05;
          letter-spacing: -0.01em;
          color: #fff;
          text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
          margin: 0;
          max-width: 24ch;
        }
        /* Force every possible rich-text wrapper the inline editor might
           save into the title token (headings, paragraphs, spans, divs,
           strong/em, links) to inherit the container's font metrics.
           Without this, editing a title through the inline editor and
           accidentally saving it wrapped in <h2>/<h3>/<h4> etc. drops the
           child element back to the browser's default heading size, so
           pages whose authors touched their hero title render smaller
           (or larger) than pages that still use the unedited <p>-wrapped
           default (e.g. the Thank You hero). Reset covers the full set of
           block-level and inline wrappers TipTap/PM can emit so every
           inner-page hero renders at the same 34/50/54px steps. */
        .hero-solid-title p,
        .hero-solid-title span,
        .hero-solid-title div,
        .hero-solid-title h1,
        .hero-solid-title h2,
        .hero-solid-title h3,
        .hero-solid-title h4,
        .hero-solid-title h5,
        .hero-solid-title h6,
        .hero-solid-title strong,
        .hero-solid-title em,
        .hero-solid-title a {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          letter-spacing: inherit;
          line-height: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-title { font-size: 50px; }
        }
        @media (min-width: 1024px) {
          .hero-solid-title { font-size: 54px; }
        }
        /* SEO h2 under the page title — keyword hierarchy companion to the
           h1, not the optional page blurb below. Sized between title and
           blurb so the three lines read as title → subtitle → body. */
        .hero-solid-seo-subtitle {
          font-family: "Montserrat", var(--vibe-font-body, sans-serif);
          font-size: 14px;
          font-weight: 400;
          line-height: 1.4;
          letter-spacing: 0.02em;
          color: #fff;
          opacity: 0.85;
          text-shadow: 0 1px 10px rgba(0, 0, 0, 0.45);
          margin: 0;
          max-width: 48ch;
        }
        .hero-solid-seo-subtitle p,
        .hero-solid-seo-subtitle span,
        .hero-solid-seo-subtitle div,
        .hero-solid-seo-subtitle h1,
        .hero-solid-seo-subtitle h2,
        .hero-solid-seo-subtitle h3,
        .hero-solid-seo-subtitle h4,
        .hero-solid-seo-subtitle h5,
        .hero-solid-seo-subtitle h6,
        .hero-solid-seo-subtitle strong,
        .hero-solid-seo-subtitle em,
        .hero-solid-seo-subtitle a {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          letter-spacing: inherit;
          line-height: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-seo-subtitle { font-size: 16px; }
        }
        @media (min-width: 1024px) {
          .hero-solid-seo-subtitle { font-size: 18px; }
        }
        .hero-solid-subtitle {
          font-family: "Montserrat", var(--vibe-font-body, sans-serif);
          font-size: 16px;
          font-weight: 300;
          line-height: 1.5;
          color: #fff;
          text-shadow: 0 1px 10px rgba(0, 0, 0, 0.5);
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-solid-subtitle { font-size: 18px; }
        }
        /* Inherit the Montserrat Light face into inline children so <strong>,
           <em>, and RichTextContent's default <p> wrapper don't fall back to
           the theme body font mid-blurb. Weight is left free so authors can
           still bold copy via the inline editor. */
        .hero-solid-subtitle p,
        .hero-solid-subtitle span,
        .hero-solid-subtitle em,
        .hero-solid-subtitle a {
          font-family: inherit;
          font-weight: inherit;
        }
        .hero-solid-subtitle p {
          margin: 0;
        }
        .hero-solid-subtitle p + p {
          margin-top: 0.5rem;
        }

        /* RealPage OLL loading fallback. The RealPage online-leasing block
           (@vibesites/blocks/blocks/realpage-oll) renders as a bare wrapper
           div with a ~877px min-height, then its vendor widgetLoader.js
           injects an <iframe> into a child container that always has id
           "OllrDiv-<sectionId>". Between the SSR paint and the iframe
           finishing its first response, browsers paint the empty iframe as
           an opaque black rectangle, which reads as a black box flashing
           above the theme's white next-section on /apply-now. Force both
           the container and the injected iframe to the theme background
           color so the loading state blends into the page instead of
           flashing black. Scoped by id prefix so the rule can't affect
           anything else in the theme. */
        [id^="OllrDiv-"],
        [id^="OllrDiv-"] iframe {
          background-color: var(--vibe-color-background, #ffffff);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.subtitle': {
      label: 'SEO Subtitle',
      type: 'richtext',
      defaultValue: '<p>Explore this page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': bgImageDef,
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
